import type { HabitFrequencyConfig } from '@/types/habits';
import { format, differenceInDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

/**
 * Calculate the total number of dots needed to display habit progress
 * based on frequency configuration.
 * Each dot represents one time unit (day, week, month, etc.)
 * A dot is filled only when the full target for that time unit is met.
 */
export function calculateTotalDots(frequencyConfig: HabitFrequencyConfig): number {
  const { frequency, duration, customDays } = frequencyConfig;

  switch (frequency) {
    case 'daily':
      // For daily habits: one dot per day
      return duration;

    case 'weekly':
      // For weekly habits: one dot per week
      return Math.ceil(duration / 7);

    case 'monthly':
      // For monthly habits: one dot per month
      return Math.ceil(duration / 30);

    case 'custom':
      // For custom habits: one dot per selected day occurrence
      const weeksInDuration = Math.ceil(duration / 7);
      return weeksInDuration * (customDays?.length || 1);

    default:
      return duration; // Fallback to duration
  }
}

/**
 * Get the time unit label based on frequency
 */
export function getTimeUnitLabel(frequencyConfig: HabitFrequencyConfig, language: 'en' | 'ar'): string {
  const { frequency } = frequencyConfig;
  
  const labels = {
    daily: language === 'ar' ? 'يوم' : 'day',
    weekly: language === 'ar' ? 'أسبوع' : 'week',
    monthly: language === 'ar' ? 'شهر' : 'month',
    custom: language === 'ar' ? 'يوم' : 'day',
  };

  return labels[frequency] || labels.daily;
}

/**
 * Calculate progress percentage based on completion count
 */
export function calculateProgressPercentage(
  completedCount: number,
  frequencyConfig: HabitFrequencyConfig
): number {
  const totalDots = calculateTotalDots(frequencyConfig);
  if (totalDots === 0) return 0;
  return Math.min(100, Math.round((completedCount / totalDots) * 100));
}

/**
 * Get display text for habit frequency
 */
export function getFrequencyDisplayText(
  frequencyConfig: HabitFrequencyConfig,
  language: 'en' | 'ar'
): string {
  const { frequency, timesPerFrequency, customDays } = frequencyConfig;

  if (language === 'ar') {
    switch (frequency) {
      case 'daily':
        return timesPerFrequency === 1 ? 'يومياً' : `${timesPerFrequency} مرات يومياً`;
      case 'weekly':
        return timesPerFrequency === 1 ? 'أسبوعياً' : `${timesPerFrequency} مرات أسبوعياً`;
      case 'monthly':
        return timesPerFrequency === 1 ? 'شهرياً' : `${timesPerFrequency} مرات شهرياً`;
      case 'custom':
        return `${timesPerFrequency}× في ${customDays?.length || 0} أيام`;
      default:
        return 'يومياً';
    }
  } else {
    switch (frequency) {
      case 'daily':
        return timesPerFrequency === 1 ? 'Daily' : `${timesPerFrequency}x per day`;
      case 'weekly':
        return timesPerFrequency === 1 ? 'Weekly' : `${timesPerFrequency}x per week`;
      case 'monthly':
        return timesPerFrequency === 1 ? 'Monthly' : `${timesPerFrequency}x per month`;
      case 'custom':
        return `${timesPerFrequency}x on ${customDays?.length || 0} days`;
      default:
        return 'Daily';
    }
  }
}

